import React, { useState, useEffect, useRef } from "react";
import { Player } from "@lottiefiles/react-lottie-player";
import { SIDEBAR } from "../../assets/asset";
import QuestionSidebar from "../QuestionSidebar/QuestionSidebar";
import Whiteboard from "../QuestionSidebar/Whiteboard";
import ExplanationModal from "../ExplanationModal";
import "../../App.css";
import CustomButton from "../../assets/buttons/CustomButton";
import CustomOptionButton, {
  getUniformOptionWidthClass,
} from "../../assets/buttons/CustomOptionButton";
import animationData from "../../utils/happy_dog_animation.json";
import { useVideoModal } from "../../hooks/useVideoModal";
import { useAudioManager } from "../../hooks/useAudioManager";

const VerticalImageMcq = ({
  questionData,
  onCorrectAnswer,
  video,
  colorTheme = "#ccc",
  questionKey,
  yearId,
}) => {
  // Hook declarations
  const { isVideoModalOpen, handleVideoModalToggle } = useVideoModal();
  const {
    question,
    imageList,
    questionimagelist,
    answer,
    questionaudio,
    explaination,
  } = questionData;

  // Standard UI state management (always in this order)
  const [isCorrect, setIsCorrect] = useState(null);
  const [isAttempt, setIsAttempt] = useState(null);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [isDelayed, setIsDelayed] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [totalHeight, setTotalHeight] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioInitialized, setAudioInitialized] = useState(false);

  // Question-specific states
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);

  // Audio hook with conditional initialization
  const { handleAudioReplay, playFeedbackAudio, stopAllAudio } =
    useAudioManager(audioInitialized ? questionaudio : null, questionKey);

  // Refs for height calculation
  const questionDivRef = useRef(null);
  const mainContentDivRef = useRef(null);
  const bottomButtonsDivRef = useRef(null);
  const totalViewRef = useRef(null);

  // Color theme system
  const colorWithOpacity = (color, opacity) => {
    const [r, g, b] = color.match(/\w\w/g).map((x) => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };
  const backgroundColor = colorWithOpacity(colorTheme, 1);
  const visibleQuestionImages = questionimagelist.filter(
    (item) => item && item !== "."
  );
  const stackedQuestionImageMaxHeight =
    visibleQuestionImages.length > 1
      ? `calc((100% - ${(visibleQuestionImages.length - 1) * 1}rem) / ${
          visibleQuestionImages.length
        })`
      : "250px";
  const optionWidthClass = getUniformOptionWidthClass(imageList);

  // Standard UI state reset
  useEffect(() => {
    // Reset UI states
    setIsCorrect(null);
    setIsAttempt(null);
    setWrongAttempts(0);
    setStartTime(new Date());
    setIsProcessing(false);
    setIsModalOpen(false);
    setShowWhiteboard(false);

    // Question-specific state resets
    setSelectedIdx(null);
    setAttemptCount(0);

    // Initialize audio after state reset
    const timer = setTimeout(() => {
      setAudioInitialized(true);
    }, 50);

    return () => {
      clearTimeout(timer);
      setAudioInitialized(false);
    };
  }, [questionKey]);

  // Video modal effect
  useEffect(() => {
    if (isVideoModalOpen) {
      stopAllAudio();
    }
  }, [isVideoModalOpen, stopAllAudio]);

  // Height calculation system
  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        questionDivRef.current &&
        mainContentDivRef.current &&
        bottomButtonsDivRef.current &&
        totalViewRef.current &&
        totalHeight === 0
      ) {
        const questionDivHeight = questionDivRef.current.offsetHeight;
        const mainContentDivHeight = mainContentDivRef.current.offsetHeight;
        const bottomButtonsDivHeight = bottomButtonsDivRef.current.offsetHeight;
        const totalHeightInPx =
          questionDivHeight + mainContentDivHeight + bottomButtonsDivHeight;
        setTotalHeight(totalHeightInPx);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [questionKey]);

  // Question-specific handlers
  const handleWhiteboardToggle = () => setShowWhiteboard(!showWhiteboard);

  const handleAnswer = (index) => {
    setIsAttempt(true);
    setSelectedIdx(index);
  };

  const resetToUnattempted = () => {
    setIsCorrect(null);
    setIsAttempt(null);
    setSelectedIdx(null);
  };

  const getUserAnswer = () => {
    return selectedIdx;
  };

  const isAnswerCorrect = (userAnswer, correctAnswer) => {
    return Number(correctAnswer[0]) === userAnswer;
  };

  const handleCorrectIncorrect = () => {
    if (selectedIdx === null || isProcessing) return;

    setIsProcessing(true);
    setAttemptCount((prev) => prev + 1);

    const userAnswer = getUserAnswer();
    const isCorrectSelection = isAnswerCorrect(userAnswer, answer);

    if (isCorrectSelection) {
      setIsCorrect(true);
      playFeedbackAudio(true);
      setIsDelayed(true);

      setTimeout(() => {
        const endTime = new Date();
        const timeTaken = (endTime - startTime) / 1000;
        onCorrectAnswer(true, attemptCount + 1, timeTaken);
        resetToUnattempted();
        setIsDelayed(false);
        setIsProcessing(false);
      }, 4000);
    } else {
      setIsCorrect(false);
      playFeedbackAudio(false);
      setWrongAttempts((prev) => {
        const newWrongAttempts = prev + 1;
        if (newWrongAttempts === 3) {
          setIsModalOpen(true);
          setTimeout(() => {
            setWrongAttempts(0);
          }, 0);
        }
        return newWrongAttempts;
      });

      setTimeout(() => {
        resetToUnattempted();
        setIsProcessing(false);
      }, 2000);
    }
  };

  // Helper functions
  const hasValidInput = () => {
    return selectedIdx !== null;
  };

  const getButtonDisabledState = () => {
    return !hasValidInput() || isDelayed || isProcessing;
  };

  const getButtonType = () => {
    if (isCorrect === true) return "correct";
    if (isCorrect === false) return "incorrect";
    if (hasValidInput()) return "selected";
    return "disabled";
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setWrongAttempts(0);
    resetToUnattempted();
  };

  return (
    <div
      className={`w-[92vw] h-[78vh] relative ${
        totalHeight <= 640 ? "mt-[-4rem]" : ""
      }`}
      ref={totalViewRef}
    >
      {/* Whiteboard Overlay */}
      {showWhiteboard && !isVideoModalOpen && (
        <div className="absolute right-0 z-10">
          <Whiteboard
            style={{ height: totalHeight }}
            onClose={handleWhiteboardToggle}
          />
        </div>
      )}

      {/* 1. Question Header Section */}
          <div
             className="flex items-center bg-opacity-75 py-4 sm:py-6 md:py-8 lg:py-10 rounded-t-[30px] shadow-lg border-2 border-[#cccccc] border-b-0"
             style={{ backgroundColor }}
             ref={questionDivRef}
           >
             <img
               src={SIDEBAR.audio}
               onClick={handleAudioReplay}
               alt="Audio"
               className="h-8 w-8 mx-6 sm:mx-8 md:mx-10 flex-shrink-0"
             />
             <div className={`text-lg sm:text-xl md:text-[22px] ${Number(yearId) >= 3 ? "text-white" : "text-black"} font-bold font-mali`}>
               {question}
             </div>
           </div>

      {/* 2. Main Content Area - KEEPING ORIGINAL LAYOUT */}
      <div
        ref={mainContentDivRef}
        className={`bg-black bg-opacity-30 ${
          !isVideoModalOpen ? "backdrop-blur-[2px]" : ""
        } flex justify-center items-center gap-6 relative h-[70%] overflow-hidden z-0 border-2 border-[#cccccc] border-t-0 border-b-0 px-10`}
      >
        {/* Fixed Sidebar */}
        <div className="absolute left-0 top-[20%]">
          <QuestionSidebar
            onAudioPlay={handleAudioReplay}
            videoUrl={video}
            SIDEBAR={SIDEBAR}
            onVideoModalToggle={handleVideoModalToggle}
            onWhiteboardToggle={handleWhiteboardToggle}
            isVideo={video !== ""}
          />
        </div>

        {/* Original Content Layout - UNCHANGED */}
        <div
          className={`flex h-full min-h-0 w-[45%] flex-col items-center gap-4 overflow-y-auto scrollable-hide-scrollbar py-6 ${
            visibleQuestionImages.length > 1
              ? "justify-start"
              : "justify-center"
          }`}
        >
          {visibleQuestionImages.length > 0 &&
            visibleQuestionImages.map((item, index) => (
              <img
                key={index}
                src={item}
                alt={`Question ${index}`}
                className="w-auto max-w-full object-contain"
                style={{ maxHeight: stackedQuestionImageMaxHeight }}
              />
            ))}
        </div>

        <div className="flex min-h-0 w-[50%] flex-row flex-wrap items-center justify-start gap-12 py-6">
          {imageList.map((item, index) => (
            <CustomOptionButton
              key={index}
              text={item}
              index={index}
              selectedIdx={selectedIdx}
              onClick={handleAnswer}
              optionWidthClass={optionWidthClass + " w-auto"}
            />
          ))}
        </div>
      </div>

      {/* 3. Bottom Action Bar */}
      {!isVideoModalOpen && (
        <div
          ref={bottomButtonsDivRef}
          className="bg-white bg-opacity-75 rounded-b-[30px] flex items-center justify-between shadow-lg py-2 px-10 sm:px-12 md:px-16 lg:px-20 border-2 border-[#cccccc] border-t-0"
          style={{ backgroundColor }}
        >
          {/* Left Side - Animation & Feedback */}
          <div className="flex justify-center items-center">
            {/* Lottie Animation */}
            <div className="flex gap-[5px] items-center">
              <Player
                autoplay
                loop
                src={animationData}
                style={{ height: "120px", width: "120px" }}
                className="sm:w-[100px] sm:h-[100px] md:w-[120px] md:h-[120px]"
              />
            </div>

            {/* Feedback Button */}
            {isCorrect !== null && (
              <CustomButton
                text={isCorrect ? "Yaay! you did it" : "Oops! try again"}
                onClick={null}
                disabled={true}
                type={isCorrect ? "correct" : "incorrect"}
              />
            )}
          </div>

          {/* Right Side - Main Action Button */}
          <CustomButton
            text={isCorrect === false ? "Try Again" : "Click to continue"}
            onClick={handleCorrectIncorrect}
            isShaking={isCorrect === false}
            disabled={getButtonDisabledState()}
            type={getButtonType()}
          />
        </div>
      )}

      {/* 4. Modals */}
      {isModalOpen && (
        <ExplanationModal
          questionData={questionData}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          explanation={explaination}
        />
      )}
    </div>
  );
};

export default VerticalImageMcq;
